<template>
  <div class="relative z-10" data-cy="widget-link">
    <div
      v-tooltip="$t('COMMON.CODE_FOR_WIDGET')"
      v-on-click-outside="() => (isMenuOpen = false)"
      @click="generateLink"
      class="ml-2 flex h-8 w-8 cursor-pointer items-center justify-center rounded-sm"
      :class="{ 'bg-blue-100': isMenuOpen }"
    >
      <icon-base
        icon="link-no-color"
        viewBox="0 0 24 24"
        height="20"
        width="20"
        :icon-color="iconColor"
      />
      <div
        v-if="isMenuOpen"
        @click.stop
        class="absolute right-0 bottom-0 w-105 translate-y-full transform cursor-default space-y-2 rounded-sm bg-white px-6 py-4 shadow-lg"
      >
        <span class="block font-semibold text-gray-700">
          {{ $t("COMMON.COPY_LINK_BELOW") }}:
        </span>
        <widget-link-field :link="widgetLink" />
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import {
  PROVIDER_CODAT,
  PROVIDER_PLAID,
  PROVIDER_TRYCHART,
  PROVIDER_VOUCHED_VERIFICATION,
  UTIL_COLORS,
} from "@/helpers/constants";
import { useDeals } from "@/hooks/deals";
import { useNotification } from "@/hooks/notifications";

import type { AuthType } from "@/enums/vouched";
</script>
<script setup lang="ts">
import WidgetLinkField from "@/views/deals/components/WidgetLinkField.vue";

const { service, type } = defineProps<{
  service: string;
  type?: AuthType;
}>();

type ServicesWithPayload = {
  [PROVIDER_VOUCHED_VERIFICATION]: {
    auth_type: AuthType | undefined;
  };
};

const actions: Record<string, string> = {
  [PROVIDER_PLAID]: "applications/generatePlaidLink",
  [PROVIDER_CODAT]: "applications/generateCodatWidgetLink",
  [PROVIDER_TRYCHART]: "getTrychartWidgetLink",
  [PROVIDER_VOUCHED_VERIFICATION]: "applications/generateVouchedWidgetLink",
};

const { t } = useI18n();
const { dispatch } = useStore();
const { activeDeal } = useDeals();
const { showMessage } = useNotification();

const isMenuOpen = ref(false);
const widgetLink = ref("");

const servicesWithPayload = computed<ServicesWithPayload>(() => ({
  [PROVIDER_VOUCHED_VERIFICATION]: {
    auth_type: type,
  },
}));

const iconColor = computed(() =>
  isMenuOpen.value ? UTIL_COLORS.PRIMARY : UTIL_COLORS.DEFAULT
);

const getServicePayload = (service: string) => {
  return servicesWithPayload.value[service as keyof ServicesWithPayload];
};

const generateLink = async () => {
  if (isMenuOpen.value) {
    isMenuOpen.value = false;
    return;
  }

  if (service === PROVIDER_TRYCHART) {
    const { try_chart } = activeDeal.value.commercialData || {};
    if (!try_chart?.tax_payer?.url) {
      return;
    }
    widgetLink.value = try_chart.tax_payer.url as string;
    isMenuOpen.value = true;
    return;
  }
  if (!widgetLink.value) {
    try {
      const payload = getServicePayload(service);
      const link = payload
        ? await dispatch(actions[service], payload)
        : await dispatch(actions[service]);
      widgetLink.value = link;
      isMenuOpen.value = true;
    } catch (error) {
      showMessage(t("COMMON.ERROR_OCCURRED", "error"));
    }
    return;
  }
  isMenuOpen.value = true;
};

watch(
  () => type,
  (updatedType) => {
    if (updatedType) {
      widgetLink.value = "";
    }
  }
);
</script>
